import { listQuestions, getExam } from '@/api/resources'

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

function correctLabel(q) {
  const { question_type: t, correct_answer: ca, options } = q
  if (!ca) return ''

  const opts = options || []

  if (t === 1) {
    const id = ca.answer ?? ca.id ?? ca
    const idx = opts.findIndex(o => o.id === id)
    return idx >= 0 ? LETTERS[idx] : String(id)
  }
  if (t === 2) {
    const ids = Array.isArray(ca.answers) ? ca.answers
              : Array.isArray(ca.ids)    ? ca.ids
              : Array.isArray(ca)        ? ca
              : null
    if (!ids) return JSON.stringify(ca)
    return ids.map(id => {
      if (typeof id === 'string') return id
      const idx = opts.findIndex(o => o.id === id)
      return idx >= 0 ? LETTERS[idx] : String(id)
    }).join(', ')
  }
  if (t === 3) {
    const val = ca.answer ?? ca
    return val === true || val === 'true' ? "To'g'ri" : "Noto'g'ri"
  }
  if (t === 5) return '' // Insho — to'g'ri javob umuman ko'rsatilmaydi
  if (t === 4) {
    if (Array.isArray(ca.answers)) return ca.answers.filter(Boolean).join(' / ')
    if (typeof ca.answer === 'string') return ca.answer
    if (typeof ca === 'string') return ca
    return ''
  }
  if (t === 7) {
    const pairs = Array.isArray(ca.pairs) ? ca.pairs : null
    if (!pairs) return JSON.stringify(ca)
    return pairs.map(p => {
      const lOpt = opts.find(o => o.id === p.left)
      const rOpt = opts.find(o => o.id === p.right)
      const l = lOpt ? lOpt.text : String(p.left)
      const r = rOpt ? rOpt.text : String(p.right)
      return `${l} → ${r}`
    }).join('; ')
  }
  if (t === 8) {
    const order = Array.isArray(ca.order) ? ca.order : null
    if (!order) return JSON.stringify(ca)
    return order.map((id, i) => {
      const opt = opts.find(o => o.id === id)
      return `${i + 1}. ${opt ? opt.text : id}`
    }).join('; ')
  }
  if (typeof ca === 'string') return ca
  if (ca.text) return ca.text
  return JSON.stringify(ca)
}

const MAIN_URL = import.meta.env.VITE_MAIN_URL || 'https://5mi.uz'

export const PDF_MODES = {
  NO_ANSWERS: 'no_answers', // javoblarsiz
  INLINE:     'inline',     // savol ostida javob
  APPENDIX:   'appendix',   // javoblar oxirgi sahifada
}

function buildHtml(exam, banks, questionsByBank, mode = PDF_MODES.INLINE) {
  const now = new Date().toLocaleDateString('uz-UZ', { year: 'numeric', month: 'long', day: 'numeric' })
  const examUrl = `${MAIN_URL}/exam/${exam.code}`
  const showInline = mode === PDF_MODES.INLINE
  const answerKey = [] // { num, content, correct } — collected when mode === APPENDIX

  let questionHtml = ''
  let globalNum = 1

  // Group banks by subject (falls back to bank name when no subject is set),
  // so multiple banks of the same subject are combined under one section.
  const subjectGroups = []
  for (const bank of banks) {
    const questions = questionsByBank[bank.question_bank] || []
    if (!questions.length) continue
    const subjectName = bank.subject_name || bank.bank_name || 'Savol bazasi'
    let group = subjectGroups.find(g => g.name === subjectName)
    if (!group) {
      group = { name: subjectName, banks: [], totalQuestions: 0, totalScore: 0 }
      subjectGroups.push(group)
    }
    group.banks.push({ bank, questions })
    group.totalQuestions += questions.length
    group.totalScore += questions.length * Number(bank.score_per_question || 0)
  }
  const hasMultipleSubjects = subjectGroups.length > 1

  subjectGroups.forEach((group, gi) => {
    const cls = ['subject-header']
    if (gi === 0) cls.push('subject-first')
    if (hasMultipleSubjects && gi > 0) cls.push('subject-break')
    questionHtml += `
      <div class="${cls.join(' ')}">
        <span>${group.name}</span>
        <span class="subject-meta">${group.totalQuestions} ta savol · ${group.totalScore} ball</span>
      </div>`

    for (const { questions } of group.banks) {
      for (const q of questions) {
      const correct = correctLabel(q)
      const opts = q.options || []

      let optionsHtml = ''
      if ([1, 2].includes(q.question_type) && opts.length) {
        optionsHtml = '<ol class="options">' +
          opts.map((o, i) => `<li><span class="opt-letter">${LETTERS[i]}</span>${o.text}</li>`).join('') +
          '</ol>'
      } else if (q.question_type === 7 && opts.length) {
        // Derive left/right from correct_answer.pairs IDs
        const pairs = q.correct_answer?.pairs
        if (Array.isArray(pairs) && pairs.length) {
          const leftIds  = pairs.map(p => p.left)
          const rightIds = pairs.map(p => p.right)
          const lefts  = leftIds.map(id => opts.find(o => o.id === id)).filter(Boolean)
          const rights = rightIds.map(id => opts.find(o => o.id === id)).filter(Boolean)
          if (lefts.length && rights.length) {
            optionsHtml = '<div class="matching-grid">' +
              '<div class="matching-col">' + lefts.map(o  => `<div class="match-item">${o.text}</div>`).join('') + '</div>' +
              '<div class="matching-col">' + rights.map(o => `<div class="match-item">${o.text}</div>`).join('') + '</div>' +
              '</div>'
          }
        }
        if (!optionsHtml) {
          optionsHtml = '<ol class="options">' +
            opts.map((o, i) => `<li><span class="opt-letter">${LETTERS[i]}</span>${o.text}</li>`).join('') +
            '</ol>'
        }
      } else if (q.question_type === 8 && opts.length) {
        optionsHtml = '<ol class="options ordering">' +
          opts.map((o, i) => `<li><span class="opt-num">${i + 1}</span>${o.text}</li>`).join('') +
          '</ol>'
      } else if ([4, 5].includes(q.question_type)) {
        const lines = q.question_type === 5 ? 6 : 2
        optionsHtml = `<div class="answer-lines">${'<div class="answer-line"></div>'.repeat(lines)}</div>`
      }

      if (mode === PDF_MODES.APPENDIX && correct) {
        answerKey.push({ num: globalNum, correct })
      }

      questionHtml += `
        <div class="question">
          <div class="q-header">
            <span class="q-num">${globalNum}.</span>
          </div>
          <div class="q-content">${q.content}</div>
          ${optionsHtml}
          ${showInline && correct ? `<div class="correct-answer"><span class="ca-label">Javob:</span> ${correct}</div>` : ''}
        </div>`
        globalNum++
      }
    }
  })

  let answerKeyHtml = ''
  if (mode === PDF_MODES.APPENDIX && answerKey.length) {
    answerKeyHtml = `
      <div class="answer-key-page">
        <div class="answer-key-title">Javoblar</div>
        <div class="answer-key-grid">
          ${answerKey.map(a => `<div class="answer-key-item"><span class="ak-num">${a.num}.</span> ${a.correct}</div>`).join('')}
        </div>
      </div>`
  }

  return `<!DOCTYPE html>
<html lang="uz">
<head>
  <meta charset="UTF-8">
  <title>${exam.title} — Savollar</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 12pt;
      color: #111;
      padding: 20mm 18mm;
      line-height: 1.55;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
      border-bottom: 2px solid #333;
      padding-bottom: 10px;
      margin-bottom: 18px;
    }
    .header-info { flex: 1; min-width: 0; }
    .exam-title { font-size: 18pt; font-weight: 700; margin-bottom: 4px; }
    .exam-meta { font-size: 9pt; color: #555; display: flex; gap: 18px; flex-wrap: wrap; }
    .exam-meta span::before { content: attr(data-label) ': '; font-weight: 600; }
    .header-qr { flex-shrink: 0; text-align: center; }
    .header-qr img { width: 84px; height: 84px; display: block; }
    .header-qr p { font-size: 7pt; color: #888; margin-top: 2px; }

    .subject-header {
      background: #1f2937;
      color: #fff;
      padding: 10px 16px;
      margin: 28px 0 14px;
      border-radius: 6px;
      font-weight: 700;
      font-size: 13pt;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .subject-header.subject-first { margin-top: 0; }
    .subject-meta { font-weight: 400; color: #cbd5e1; font-size: 9.5pt; }
    .subject-break { break-before: page; }

    .question {
      margin-bottom: 18px;
      break-inside: avoid;
      padding-left: 4px;
    }
    .q-header {
      display: flex;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 4px;
    }
    .q-num { font-weight: 700; font-size: 11pt; min-width: 24px; }
    .q-content { margin-bottom: 8px; padding-left: 24px; }

    .options {
      list-style: none;
      padding-left: 28px;
      margin-bottom: 6px;
    }
    .options li {
      display: flex;
      align-items: baseline;
      gap: 8px;
      padding: 2px 0;
      font-size: 10.5pt;
    }
    .opt-letter {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      border: 1.5px solid #333;
      border-radius: 50%;
      font-size: 9pt;
      font-weight: 600;
      flex-shrink: 0;
    }
    .opt-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      background: #e5e7eb;
      border-radius: 50%;
      font-size: 9pt;
      font-weight: 600;
      flex-shrink: 0;
      color: #444;
    }
    .ordering li { cursor: default; }

    .matching-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      padding-left: 28px;
      margin-bottom: 6px;
    }
    .matching-col { display: flex; flex-direction: column; gap: 4px; }
    .match-item {
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 10.5pt;
    }

    .answer-lines { padding-left: 28px; margin-bottom: 4px; }
    .answer-line {
      border-bottom: 1px solid #bbb;
      height: 24px;
      margin-bottom: 4px;
    }

    .correct-answer {
      margin-left: 28px;
      margin-top: 4px;
      font-size: 10pt;
      color: #16a34a;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 4px;
      padding: 4px 10px;
      display: inline-block;
    }
    .ca-label { font-weight: 600; margin-right: 4px; }

    .answer-key-page {
      break-before: page;
      padding-top: 4px;
    }
    .answer-key-title {
      font-size: 16pt;
      font-weight: 700;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 2px solid #333;
    }
    .answer-key-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px 20px;
    }
    .answer-key-item {
      font-size: 10.5pt;
      padding: 4px 0;
      border-bottom: 1px dotted #ccc;
    }
    .ak-num { font-weight: 700; margin-right: 4px; }

    .footer {
      margin-top: 24px;
      border-top: 1px solid #ddd;
      padding-top: 8px;
      font-size: 8.5pt;
      color: #999;
      text-align: center;
    }
    .watermark {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-35deg);
      font-size: 13pt;
      font-weight: 600;
      color: rgba(0,0,0,0.07);
      white-space: nowrap;
      pointer-events: none;
      z-index: 0;
      letter-spacing: 1px;
      user-select: none;
    }
    @media print {
      body { padding: 10mm 12mm; }
      .question { break-inside: avoid; }
      .watermark { position: fixed; top: 50%; left: 50%; }
    }
  </style>
</head>
<body>
  <div class="watermark">${examUrl}</div>
  <div class="header">
    <div class="header-info">
      <div class="exam-title">${exam.title}</div>
      <div class="exam-meta">
        <span data-label="Kod">${exam.code || '—'}</span>
        ${exam.time_limit_minutes ? `<span data-label="Vaqt">${exam.time_limit_minutes} daqiqa</span>` : ''}
        <span data-label="Sana">${now}</span>
      </div>
    </div>
    <div class="header-qr">
      <img src="https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(examUrl)}" alt="QR" />
      <p>Kirish uchun skanerlang</p>
    </div>
  </div>

  ${questionHtml || '<p style="color:#999;padding:20px 0">Savollar topilmadi.</p>'}

  ${answerKeyHtml}

  <div class="footer">5mi.uz — ${exam.title} (${exam.code}) · ${now}</div>
  <script>window.onload = () => window.print()<\/script>
</body>
</html>`
}

export async function exportExamPdf(examPartial, mode = PDF_MODES.INLINE) {
  // Open window immediately (before any await) to preserve user-gesture context
  const win = window.open('', '_blank')
  if (!win) { alert("Pop-up bloklangan. Brauzerda pop-up ruxsatini yoqing."); return }

  win.document.write('<html><body style="font-family:sans-serif;padding:40px;color:#555">Yuklanmoqda...</body></html>')

  try {
    let exam = examPartial
    if (!exam.banks) {
      const { data } = await getExam(exam.code)
      exam = data
    }
    const banks = exam.banks || []
    const questionsByBank = {}

    await Promise.all(banks.map(async (bank) => {
      const bankId = bank.question_bank
      let page = 1
      let all = []
      while (true) {
        const { data } = await listQuestions({ question_bank: bankId, page_size: 100, page })
        const results = data.results ?? data
        all = all.concat(results)
        if (!data.next) break
        page++
      }
      questionsByBank[bankId] = all
    }))

    const html = buildHtml(exam, banks, questionsByBank, mode)
    win.document.open()
    win.document.write(html)
    win.document.close()
  } catch (err) {
    win.document.write(`<html><body style="font-family:sans-serif;padding:40px;color:red">Xatolik: ${err.message}</body></html>`)
  }
}
